import { Grid } from '@mui/material';
import { CustomFieldDefinition } from '@nx-smartmonkey/shared/domain';
import { dateFormat, pastDateTime } from '@nx-smartmonkey/shared/helpers';
import { RoutalPalette, RoutalTooltip } from '@nx-smartmonkey/ui';
import { TableColumn } from '../../../context/shared/tableColumns';
import Text from '../../../react-components/Text';
import { DataTableColumnProps } from '../../../react-components/data-table/DataTable';
import { ModelToIntlId, TypeToIntlId } from './IntlMapper';

export const customFieldsTableColumnKeys = [
  `label`,
  `category`,
  `field_type`,
  `custom_id`,
  `updated_at`,
  `created_at`,
] as const;

export type CustomFieldsTableColumnKeys = (typeof customFieldsTableColumnKeys)[number];

export type StopsTableColumn = TableColumn<CustomFieldsTableColumnKeys, CustomFieldDefinition>;

type CustomFieldsColumnsResponse = { [key: string]: any }[];

export const tableColumns: Array<StopsTableColumn> = [
  {
    identifier: `label`,
    type: `string`,
    displayable: true,
    filterable: true,
    sortable: true,
  },
  {
    identifier: `category`,
    type: `string`,
    displayable: true,
    filterable: true,
    sortable: true,
  },
  {
    identifier: `field_type`,
    type: `string`,
    displayable: true,
    filterable: true,
    sortable: true,
  },
  {
    identifier: `custom_id`,
    type: `string`,
    displayable: true,
    filterable: true,
    sortable: true,
  },
  {
    identifier: `updated_at`,
    type: `Date`,
    displayable: true,
    filterable: true,
    sortable: true,
  },
  {
    identifier: `created_at`,
    type: `Date`,
    displayable: true,
    filterable: true,
    sortable: true,
  },
];

export const getCustomFieldsColumns = (): CustomFieldsColumnsResponse => {
  const columns: Array<DataTableColumnProps> = [];

  tableColumns.forEach(({ displayable, identifier, sortable }: StopsTableColumn) => {
    if (displayable) {
      switch (identifier) {
        case `label`:
          columns.push({
            width: 350,
            accessorName: `label`,
            name: (
              <Text
                variant="body"
                style={{ color: RoutalPalette.primary20 }}
                intlId="projects.settings.fields.column.name"
              />
            ),
            sortable,
            formatter: ({ doc }) => (
              <Grid container direction="column" wrap="nowrap">
                <Grid item container direction="row">
                  <Grid item>
                    {doc.description ? (
                      <RoutalTooltip
                        id="field-description"
                        placement="top"
                        overlayStyle={{
                          maxWidth: `300px`,
                        }}
                        title={doc.description}
                      >
                        <Text variant="body"> {doc.label} </Text>
                      </RoutalTooltip>
                    ) : (
                      <Text variant="body"> {doc.label} </Text>
                    )}
                  </Grid>
                </Grid>
              </Grid>
            ),
          });
          break;
        case `category`:
          columns.push({
            width: 100,
            accessorName: `category`,
            name: (
              <Text
                variant="body"
                style={{ color: RoutalPalette.primary20 }}
                intlId="projects.settings.fields.column.category"
              />
            ),
            sortable,
            columnStyle: { justifyContent: `center` },
            formatter: ({ doc }: { doc: CustomFieldDefinition }) => (
              <Grid container direction="column" wrap="nowrap">
                <Grid item container direction="row">
                  <Grid item>
                    <Text variant="body" intlId={ModelToIntlId[doc.field_model]} />
                  </Grid>
                </Grid>
              </Grid>
            ),
          });
          break;
        case `field_type`:
          columns.push({
            width: 100,
            accessorName: `field_type`,
            name: (
              <Text
                variant="body"
                style={{ color: RoutalPalette.primary20 }}
                intlId="projects.settings.fields.column.field_type"
              />
            ),
            sortable,
            columnStyle: { justifyContent: `center` },
            formatter: ({ doc }: { doc: CustomFieldDefinition }) => {
              const docType =
                doc.field_type === `categorical` && doc.multiple ? `categorical_multiple` : doc.field_type;

              return (
                <Grid container direction="column" wrap="nowrap">
                  <Grid item container direction="row">
                    <Grid item>
                      <Text variant="body" intlId={TypeToIntlId[docType]} />
                    </Grid>
                  </Grid>
                </Grid>
              );
            },
          });
          break;
        case `custom_id`:
          columns.push({
            width: 100,
            accessorName: `custom_id`,
            name: (
              <Text
                variant="body"
                style={{ color: RoutalPalette.primary20 }}
                intlId="projects.settings.fields.column.id"
              />
            ),
            sortable,
            columnStyle: { justifyContent: `center` },
            formatter: ({ doc }: { doc: CustomFieldDefinition }) => (
              <Grid container direction="column" wrap="nowrap">
                <Grid item container direction="row">
                  <Grid item>
                    <Text variant="body">{doc.custom_id}</Text>
                  </Grid>
                </Grid>
              </Grid>
            ),
          });
          break;
        case `updated_at`:
          columns.push({
            width: 100,
            accessorName: `updated_at`,
            name: (
              <Text
                variant="body"
                style={{ color: RoutalPalette.primary20 }}
                intlId="projects.settings.fields.column.updated_at"
              />
            ),
            sortable,
            columnStyle: { justifyContent: `center` },
            formatter: ({ doc }: { doc: CustomFieldDefinition }) => (
              <Grid container direction="column" wrap="nowrap">
                <Grid item container direction="row">
                  <Grid item>
                    <RoutalTooltip title={dateFormat(doc.updated_at)}>
                      <Text variant="body">{pastDateTime(doc.updated_at)}</Text>
                    </RoutalTooltip>
                  </Grid>
                </Grid>
              </Grid>
            ),
          });
          break;
        case `created_at`:
          columns.push({
            width: 100,
            accessorName: `created_at`,
            name: (
              <Text
                variant="body"
                style={{ color: RoutalPalette.primary20 }}
                intlId="projects.settings.fields.column.created_at"
              />
            ),
            sortable,
            columnStyle: { justifyContent: `center` },
            formatter: ({ doc }: { doc: CustomFieldDefinition }) => (
              <Grid container direction="column" wrap="nowrap">
                <Grid item container direction="row">
                  <Grid item>
                    <RoutalTooltip title={dateFormat(doc.created_at)}>
                      <Text variant="body">{pastDateTime(doc.created_at)}</Text>
                    </RoutalTooltip>
                  </Grid>
                </Grid>
              </Grid>
            ),
          });
          break;
      }
    }
  });

  return columns;
};
